import { useMemo, useState } from 'react';
import {
  KeyRound,
  UserPlus,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Mail,
} from 'lucide-react';
import { UserRole } from '@pms/shared';
import { SlideOver } from '@/components/ui/SlideOver';
import { CredentialsBlock } from '@/components/CredentialsBlock';
import { useCreateOperatorAdmin, usePlatformOperators } from '@/hooks/api/usePlatform';
import { extractErrorMessage } from '@/lib/error';
import { userDisplayName } from '@/lib/user-display';
import type {
  CreateOperatorAdminRequest,
  CreateOperatorAdminResult,
} from '@/services/api/platform.api';

/**
 * Per-property super-admin action: grant a property's hotelier team a PMS login.
 * Calls POST /admin/platform/operators/:tenantId/admins (via useCreateOperatorAdmin),
 * which mints a one-time temp password, forces a first-login reset, and emails a
 * branded invite. The tenantId is the property's operator (tenant) — resolved from
 * the operators directory by the property's operatorName.
 *
 * This is a property-scoped action only — it never reintroduces a global
 * Operators/Users nav surface.
 */

// Assignable roles (super_admin can never be minted here — the backend rejects it).
const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: UserRole.ADMIN, label: 'Admin — full access to this property' },
  { value: UserRole.OWNER, label: 'Owner — owner dashboard' },
  { value: UserRole.REVENUE_MANAGER, label: 'Revenue manager — rates, availability & reports' },
  { value: UserRole.OPERATIONS, label: 'Operations — front desk & housekeeping' },
];

const fieldClass =
  'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:bg-gray-50 disabled:text-gray-400';

function SuccessPanel({
  result,
  groupCode,
  onAddAnother,
}: {
  result: CreateOperatorAdminResult;
  /** The operator's group code (tenant slug) — shown for username-only logins. */
  groupCode: string | undefined;
  onAddAnother: () => void;
}) {
  const isUsernameOnly = !result.user.email && !!result.user.username;
  const label = userDisplayName(result.user);

  return (
    <div
      className="rounded-xl border border-green-200 bg-green-50 p-5"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <CheckCircle2 size={20} className="text-green-600 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-green-900">
            PMS access created for {label}
          </p>
          {isUsernameOnly ? (
            <p className="mt-1 text-sm text-green-800">
              No email sent — share these credentials directly. They&apos;ll be
              prompted to set a new password on first login.
            </p>
          ) : (
            <>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-green-800">
                <Mail size={14} className="shrink-0" aria-hidden="true" />
                An invite email with sign-in details was sent to {result.user.email}.
              </p>
              <p className="mt-2 text-sm text-green-800">
                Share this one-time password with them too (in case the email
                doesn&apos;t arrive) — they&apos;ll be prompted to set a new one on
                first login.
              </p>
            </>
          )}

          <div className="mt-3">
            <CredentialsBlock
              group={isUsernameOnly ? groupCode : undefined}
              username={result.user.username}
              password={result.temporaryPassword}
            />
          </div>

          <p className="mt-3 text-xs text-green-700">
            The password is shown once and can&apos;t be retrieved later.
          </p>

          <button
            type="button"
            onClick={onAddAnother}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
          >
            <UserPlus size={15} aria-hidden="true" />
            Add another login
          </button>
        </div>
      </div>
    </div>
  );
}

interface SetupPmsAccessButtonProps {
  /** The property this login will manage (used for copy only). */
  propertyName: string;
  /**
   * The property's operator (tenant) name, from the platform property detail.
   * Used to resolve the tenantId the login is created under.
   */
  operatorName: string | undefined;
}

export function SetupPmsAccessButton({
  propertyName,
  operatorName,
}: SetupPmsAccessButtonProps) {
  const { data: operators, isLoading: operatorsLoading } = usePlatformOperators();
  const createUser = useCreateOperatorAdmin();

  const [isOpen, setIsOpen] = useState(false);
  const [identityMode, setIdentityMode] = useState<'email' | 'username'>('email');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.ADMIN);
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateOperatorAdminResult | null>(null);

  // Resolve the operator (tenant) from the operators directory: match on the
  // property's operator name; if there's only one operator, fall back to it.
  const operator = useMemo(() => {
    if (!operators) return undefined;
    const match = operatorName
      ? operators.find((o) => o.name === operatorName)
      : undefined;
    if (match) return match;
    if (operators.length === 1) return operators[0];
    return undefined;
  }, [operators, operatorName]);
  const tenantId = operator?.tenantId;

  function resetForm() {
    setIdentityMode('email');
    setEmail('');
    setUsername('');
    setFirstName('');
    setLastName('');
    setRole(UserRole.ADMIN);
    setFormError(null);
    setResult(null);
  }

  function open() {
    resetForm();
    setIsOpen(true);
  }

  function close() {
    setIsOpen(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!tenantId) {
      setFormError('Could not determine this property’s operator. Try again shortly.');
      return;
    }
    if (identityMode === 'email' && !email.trim()) {
      setFormError('Enter an email address.');
      return;
    }
    if (identityMode === 'username' && !username.trim()) {
      setFormError('Enter a username.');
      return;
    }

    const identity: Pick<CreateOperatorAdminRequest, 'email' | 'username'> =
      identityMode === 'email'
        ? { email: email.trim() }
        : { username: username.trim() };

    createUser.mutate(
      {
        tenantId,
        body: {
          ...identity,
          role,
          ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
          ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
        },
      },
      {
        onSuccess: (data) => setResult(data),
        onError: (err) => setFormError(extractErrorMessage(err)),
      },
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={open}
        className="inline-flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3.5 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <KeyRound size={15} aria-hidden="true" />
        Set up PMS access
      </button>

      <SlideOver isOpen={isOpen} onClose={close} title="Set up PMS access">
        <div className="space-y-5">
          <p className="text-sm text-gray-600">
            Create a PMS login for the team that runs{' '}
            <span className="font-medium text-gray-900">{propertyName}</span>. They&apos;ll
            get a branded invite email with a temporary password and be asked to reset it
            on first login.
          </p>

          {result ? (
            <SuccessPanel
              result={result}
              groupCode={operator?.slug}
              onAddAnother={resetForm}
            />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-1">
                  Identity <span className="text-red-500">*</span>
                </legend>
                <div
                  className="flex flex-wrap gap-4"
                  role="radiogroup"
                  aria-label="Identity"
                >
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="pms-access-identity"
                      value="email"
                      checked={identityMode === 'email'}
                      onChange={() => setIdentityMode('email')}
                      disabled={createUser.isPending}
                      className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    Email
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="pms-access-identity"
                      value="username"
                      checked={identityMode === 'username'}
                      onChange={() => setIdentityMode('username')}
                      disabled={createUser.isPending}
                      className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    Username-only (no email)
                  </label>
                </div>
              </fieldset>

              {identityMode === 'email' ? (
                <div>
                  <label
                    htmlFor="pms-access-email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="pms-access-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={createUser.isPending}
                    required
                    autoComplete="off"
                    placeholder="owner@happystayinn.com"
                    className={fieldClass}
                  />
                </div>
              ) : (
                <div>
                  <label
                    htmlFor="pms-access-username"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="pms-access-username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={createUser.isPending}
                    required
                    autoComplete="off"
                    autoCapitalize="none"
                    placeholder="reception"
                    aria-describedby="pms-access-username-help"
                    className={fieldClass}
                  />
                  <p
                    id="pms-access-username-help"
                    className="mt-1 text-xs text-gray-400"
                  >
                    Unique within {operator ? operator.name : 'the group'}. Login =
                    group code + username. No invite email is sent.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="pms-access-first-name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    First name{' '}
                    <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    id="pms-access-first-name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={createUser.isPending}
                    autoComplete="off"
                    placeholder="Priya"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label
                    htmlFor="pms-access-last-name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Last name{' '}
                    <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    id="pms-access-last-name"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={createUser.isPending}
                    autoComplete="off"
                    placeholder="Sharma"
                    className={fieldClass}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="pms-access-role"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  id="pms-access-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as UserRole)}
                  disabled={createUser.isPending}
                  className={fieldClass}
                >
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400">
                  Super-admin can&apos;t be assigned here.
                </p>
              </div>

              {formError && (
                <div
                  className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                  role="alert"
                >
                  <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{formError}</span>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={close}
                  disabled={createUser.isPending}
                  className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createUser.isPending || operatorsLoading || !tenantId}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {createUser.isPending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                      Creating…
                    </>
                  ) : (
                    <>
                      <KeyRound size={16} aria-hidden="true" />
                      Create login &amp; send invite
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </SlideOver>
    </>
  );
}
